import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Space,
  Tag, message, Tooltip, Typography, Badge, Tabs, Alert, Popconfirm,
} from 'antd';
import {
  EditOutlined, ReloadOutlined, UserOutlined, TeamOutlined,
  StopOutlined, UndoOutlined,
} from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { fetchWithRetry } from '../utils/fetchWithRetry';

const { Text } = Typography;
const { TextArea } = Input;
const BASE = process.env.REACT_APP_API_URL;

interface AwardRecord {
  id: number;
  approvalId: string;
  rewardName: string;
  nomineeName: string;
  nomineeEmail: string;
  nomineeEmployeeId: string;
  nomineeLegalEntityName: string;
  nomineeCountry: string;
  nomineeCity: string;
  nomineeDesignation: string;
  nomineeGrade: string;
  nomineeUnit: string;
  nomineeBu: string;
  nomineeProductName: string;
  teamName: string;
  teamMembers: string;
  nominatorName: string;
  nominatorEmail: string;
  nominatorEmployeeId: string;
  nominatorLegalEntityName: string;
  nominatorCountry: string;
  nominatorCity: string;
  nominatorDesignation: string;
  nominatorGrade: string;
  nominatorUnit: string;
  nominatorProductName: string;
  nominatorCitation: string;
  nominatorComments: string;
  managerName: string;
  managerId: string;
  managerEmail: string;
  managerComments: string;
  managerVote: string;
  firstLevelApproverName: string;
  firstLevelApproverEmployeeId: string;
  firstLevelApproverEmail: string;
  firstLevelApproverUnit: string;
  firstLevelApproverComments: string;
  firstLevelApproverVote: string;
  secondLevelApproverName: string;
  secondLevelApproverEmailId: string;
  secondLevelApproverComments: string;
  secondLevelApproverVote: string;
  eltAverageScore: string;
  finalLevelApproverName: string;
  finalLevelApproverEmail: string;
  finalLevelApproverComments: string;
  finalLevelApproverVote: string;
  formFields: string;
  campaignName: string;
  elt1Name: string; elt1Email: string; elt1Response: string; elt1Score: string;
  elt2Name: string; elt2Email: string; elt2Response: string; elt2Score: string;
  elt3Name: string; elt3Email: string; elt3Response: string; elt3Score: string;
  elt4Name: string; elt4Email: string; elt4Response: string; elt4Score: string;
  elt5Name: string; elt5Email: string; elt5Response: string; elt5Score: string;
  elt6Name: string; elt6Email: string; elt6Response: string; elt6Score: string;
  elt7Name: string; elt7Email: string; elt7Response: string; elt7Score: string;
  elt8Name: string; elt8Email: string; elt8Response: string; elt8Score: string;
  elt9Name: string; elt9Email: string; elt9Response: string; elt9Score: string;
  elt10Name: string; elt10Email: string; elt10Response: string; elt10Score: string;
  elt11Name: string; elt11Email: string; elt11Response: string; elt11Score: string;
  elt12Name: string; elt12Email: string; elt12Response: string; elt12Score: string;
  withdrawnAt: string; withdrawnBy: string; withdrawnReason: string;
}

const voteColor = (vote: string) => {
  if (vote === 'Approved') return 'success';
  if (vote === 'Rejected') return 'error';
  if (vote === 'On Hold') return 'warning';
  return 'default';
};

const G: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>{children}</div>
);

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div style={{
    background: '#8629A4', color: '#fff', fontWeight: 600, fontSize: 13,
    padding: '6px 12px', borderRadius: 4, marginBottom: 12,
  }}>{title}</div>
);

const VOTE_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'On Hold', label: 'On Hold' },
  { value: 'Not Reviewed', label: 'Not Reviewed' },
];

const ELT_RESPONSE_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'Support', label: 'Support' },
  { value: 'Reject', label: 'Reject' },
  { value: 'No Visibility', label: 'No Visibility' },
];

type ApproverCheck = '' | 'noUnitHead' | 'noElt' | 'noFinal' | 'noPanel';
const APPROVER_CHECKS: { value: ApproverCheck; label: string; test: (r: AwardRecord) => boolean }[] = [
  { value: 'noUnitHead', label: 'Unit Head not set',      test: r => !r.firstLevelApproverEmail },
  { value: 'noElt',      label: 'ELT approver not set',   test: r => !r.secondLevelApproverEmailId },
  { value: 'noFinal',    label: 'Final approver not set', test: r => !r.finalLevelApproverEmail },
  { value: 'noPanel',    label: 'ELT panel not set',      test: r => !r.elt1Email },
];

const AceAviaAwards: React.FC = () => {
  const { instance, accounts } = useMsal();

  const [records, setRecords]         = useState<AwardRecord[]>([]);
  const [filtered, setFiltered]       = useState<AwardRecord[]>([]);
  const [loading, setLoading]         = useState(false);
  const [modalOpen, setModalOpen]     = useState(false);
  const [editing, setEditing]         = useState<AwardRecord | null>(null);
  const [saving, setSaving]           = useState(false);
  const [pageSize, setPageSize]       = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch]           = useState('');
  const [rewardFilter, setRewardFilter] = useState<string>('');
  const [approverCheck, setApproverCheck] = useState<ApproverCheck>('');
  // Which campaign's nominations to show; null until the campaign list has loaded
  const [campaigns, setCampaigns] = useState<{ id: number; campaignName: string | null; campaignStatus: string | null; nominationCount: number }[]>([]);
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [eltDefaults, setEltDefaults] = useState<Record<number, { name: string; email: string }>>({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [form] = Form.useForm();
  const [show, setShow] = useState<'active' | 'withdrawn' | 'all'>('active');
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawForm] = Form.useForm();

  const fetchToken = useCallback(async () => {
    const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return res.idToken;
  }, [instance, accounts]);

  // Campaign list for the selector; starts on the active campaign
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await fetchToken();
        const res = await fetchWithRetry(`${BASE}/api/campaigns`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error();
        const list = await res.json();
        if (cancelled) return;
        setCampaigns(list ?? []);
        const active = (list ?? []).find((c: any) => c.campaignStatus === 'active') ?? (list ?? [])[0];
        setCampaignId(active ? active.id : 0); // 0 = no campaigns: show the active-campaign default
      } catch {
        if (!cancelled) { message.error('Failed to load campaigns.'); setCampaignId(0); }
      }
    })();
    return () => { cancelled = true; };
  }, [fetchToken]);

  const loadRecords = useCallback(async () => {
    if (campaignId === null) return; // wait for the campaign list
    setLoading(true);
    try {
      const token = await fetchToken();
      const query = `?includeWithdrawn=true${campaignId ? `&campaignId=${campaignId}` : ''}`;
      const res = await fetchWithRetry(`${BASE}/api/nominations/awards/list${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      const s = (v: any) => v ?? '';
      const rows: AwardRecord[] = (data.responseData ?? data ?? []).map((r: any) => ({
        id: r.id,
        approvalId: s(r.approvalId), rewardName: s(r.rewardName),
        nomineeName: s(r.nomineeName), nomineeEmail: s(r.nomineeEmail),
        nomineeEmployeeId: s(r.nomineeEmployeeId), nomineeLegalEntityName: s(r.nomineeLegalEntityName),
        nomineeCountry: s(r.nomineeCountry), nomineeCity: s(r.nomineeCity),
        nomineeDesignation: s(r.nomineeDesignation), nomineeGrade: s(r.nomineeGrade),
        nomineeUnit: s(r.nomineeUnit), nomineeBu: s(r.nomineeBu), nomineeProductName: s(r.nomineeProductName),
        teamName: s(r.teamName), teamMembers: s(r.teamMembers),
        nominatorName: s(r.nominatorName), nominatorEmail: s(r.nominatorEmail),
        nominatorEmployeeId: s(r.nominatorEmployeeId), nominatorLegalEntityName: s(r.nominatorLegalEntityName),
        nominatorCountry: s(r.nominatorCountry), nominatorCity: s(r.nominatorCity),
        nominatorDesignation: s(r.nominatorDesignation), nominatorGrade: s(r.nominatorGrade),
        nominatorUnit: s(r.nominatorUnit), nominatorProductName: s(r.nominatorProductName),
        nominatorCitation: s(r.nominatorCitation), nominatorComments: s(r.nominatorComments),
        managerName: s(r.managerName), managerId: s(r.managerId),
        managerEmail: s(r.managerEmail), managerComments: s(r.managerComments), managerVote: s(r.managerVote),
        firstLevelApproverName: s(r.firstLevelApproverName), firstLevelApproverEmployeeId: s(r.firstLevelApproverEmployeeId),
        firstLevelApproverEmail: s(r.firstLevelApproverEmail), firstLevelApproverUnit: s(r.firstLevelApproverUnit),
        firstLevelApproverComments: s(r.firstLevelApproverComments), firstLevelApproverVote: s(r.firstLevelApproverVote),
        secondLevelApproverName: s(r.secondLevelApproverName), secondLevelApproverEmailId: s(r.secondLevelApproverEmailId),
        secondLevelApproverComments: s(r.secondLevelApproverComments), secondLevelApproverVote: s(r.secondLevelApproverVote),
        eltAverageScore: s(r.eltAverageScore),
        finalLevelApproverName: s(r.finalLevelApproverName), finalLevelApproverEmail: s(r.finalLevelApproverEmail),
        finalLevelApproverComments: s(r.finalLevelApproverComments), finalLevelApproverVote: s(r.finalLevelApproverVote),
        formFields: s(r.formFields), campaignName: s(r.campaignName),
        elt1Name: s(r.elt1Name), elt1Email: s(r.elt1Email), elt1Response: s(r.elt1Response), elt1Score: s(r.elt1Score),
        elt2Name: s(r.elt2Name), elt2Email: s(r.elt2Email), elt2Response: s(r.elt2Response), elt2Score: s(r.elt2Score),
        elt3Name: s(r.elt3Name), elt3Email: s(r.elt3Email), elt3Response: s(r.elt3Response), elt3Score: s(r.elt3Score),
        elt4Name: s(r.elt4Name), elt4Email: s(r.elt4Email), elt4Response: s(r.elt4Response), elt4Score: s(r.elt4Score),
        elt5Name: s(r.elt5Name), elt5Email: s(r.elt5Email), elt5Response: s(r.elt5Response), elt5Score: s(r.elt5Score),
        elt6Name: s(r.elt6Name), elt6Email: s(r.elt6Email), elt6Response: s(r.elt6Response), elt6Score: s(r.elt6Score),
        elt7Name: s(r.elt7Name), elt7Email: s(r.elt7Email), elt7Response: s(r.elt7Response), elt7Score: s(r.elt7Score),
        elt8Name: s(r.elt8Name), elt8Email: s(r.elt8Email), elt8Response: s(r.elt8Response), elt8Score: s(r.elt8Score),
        elt9Name: s(r.elt9Name), elt9Email: s(r.elt9Email), elt9Response: s(r.elt9Response), elt9Score: s(r.elt9Score),
        elt10Name: s(r.elt10Name), elt10Email: s(r.elt10Email), elt10Response: s(r.elt10Response), elt10Score: s(r.elt10Score),
        elt11Name: s(r.elt11Name), elt11Email: s(r.elt11Email), elt11Response: s(r.elt11Response), elt11Score: s(r.elt11Score),
        elt12Name: s(r.elt12Name), elt12Email: s(r.elt12Email), elt12Response: s(r.elt12Response), elt12Score: s(r.elt12Score),
        withdrawnAt: s(r.withdrawnAt), withdrawnBy: s(r.withdrawnBy), withdrawnReason: s(r.withdrawnReason),
      }));
      setRecords(rows);
      setFiltered(rows);
    } catch {
      message.error('Failed to load awards.');
    } finally {
      setLoading(false);
    }
  }, [fetchToken, campaignId]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  // ELT members from Admin > ELT Emails, by slot: key elt_<n>_email -> ELT Member n
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await fetchToken();
        const res = await fetchWithRetry(`${BASE}/api/nominations/elt/list`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        const map: Record<number, { name: string; email: string }> = {};
        (data.responseData ?? data ?? []).forEach((r: any) => {
          const key: string = r.eltEmailKey ?? r.elt_email_key ?? '';
          const email: string = (r.eltEmail1 ?? r.elt_email ?? '').trim();
          const n = Number(/^elt_?(\d+)/i.exec(key)?.[1]);
          if (email && n >= 1 && n <= 12 && !map[n]) map[n] = { name: r.displayName ?? '', email };
        });
        if (!cancelled) setEltDefaults(map);
      } catch { /* defaults are optional */ }
    })();
    return () => { cancelled = true; };
  }, [fetchToken]);

  // A different campaign = a different set of rows; drop the selection
  useEffect(() => { setSelectedIds([]); }, [campaignId]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(records.filter(r => {
      const matchSearch = !q ||
        r.nomineeName.toLowerCase().includes(q) ||
        r.nomineeEmail.toLowerCase().includes(q) ||
        r.nominatorName.toLowerCase().includes(q) ||
        r.approvalId.toLowerCase().includes(q) ||
        r.teamName.toLowerCase().includes(q);
      const matchReward = !rewardFilter || r.rewardName === rewardFilter;
      const check = APPROVER_CHECKS.find(c => c.value === approverCheck);
      const matchCheck = !check || check.test(r);
      const matchShow = show === 'all' || (show === 'withdrawn' ? !!r.withdrawnAt : !r.withdrawnAt);
      return matchSearch && matchReward && matchCheck && matchShow;
    }));
    setCurrentPage(1);
  }, [search, rewardFilter, approverCheck, show, records]);

  // Drop selections that no longer exist after a reload
  useEffect(() => {
    setSelectedIds(ids => ids.filter(id => records.some(r => r.id === id)));
  }, [records]);

  const selectedRecords = records.filter(r => selectedIds.includes(r.id));
  const activeRecords = records.filter(r => !r.withdrawnAt);
  const selectedActive = selectedRecords.filter(r => !r.withdrawnAt);
  const selectedWithdrawn = selectedRecords.filter(r => !!r.withdrawnAt);

  /* ── Withdraw nominations that are not required; Restore brings them back as they were ── */
  const openWithdraw = () => {
    withdrawForm.resetFields();
    setWithdrawOpen(true);
  };

  const saveWithdraw = async () => {
    let v: { reason: string };
    try { v = await withdrawForm.validateFields(); } catch { return; }
    await bulkPut('/api/nominations/approvers/withdraw',
      { awardIds: selectedActive.map(r => r.id), reason: v.reason },
      'Withdrawn', () => { setWithdrawOpen(false); setSelectedIds([]); });
  };

  const restore = async (ids: number[]) => {
    await bulkPut('/api/nominations/approvers/restore', { awardIds: ids },
      'Restored', () => setSelectedIds(prev => prev.filter(id => !ids.includes(id))));
  };

  const bulkPut = async (path: string, body: unknown, done: string, close: () => void) => {
    setBulkSaving(true);
    try {
      const token = await fetchToken();
      const res = await fetchWithRetry(`${BASE}${path}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.text()).replace(/^"|"$/g, '') || 'Update failed.');
      const data = await res.json();
      message.success(`${done} on ${data.updated} nomination${data.updated === 1 ? '' : 's'}.`);
      close();
      loadRecords();
    } catch (err: any) {
      message.error(err.message || 'Update failed.');
    } finally {
      setBulkSaving(false);
    }
  };

  const openEdit = (record: AwardRecord) => {
    setEditing(record);
    // ELT Calibration: an empty slot defaults to the ELT member set for it on ELT Emails
    // (key elt_<n>_email); slots that already have someone are left as they are.
    const defaults: Record<string, string> = {};
    Object.entries(eltDefaults).forEach(([n, m]) => {
      if (!(record as any)[`elt${n}Email`]) {
        defaults[`elt${n}Email`] = m.email;
        defaults[`elt${n}Name`] = (record as any)[`elt${n}Name`] || m.name;
      }
    });
    form.setFieldsValue({ ...record, ...defaults });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values: any;
    try { values = await form.validateFields(); }
    catch { return; }
    setSaving(true);
    try {
      const token = await fetchToken();
      const res = await fetchWithRetry(`${BASE}/api/nominations/awards/${editing!.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error(await res.text());
      message.success('Award updated successfully.');
      setModalOpen(false);
      loadRecords();
    } catch (err: any) {
      message.error(err.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const rewardTypes = records.map(r => r.rewardName).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
  const isTeam = editing?.rewardName?.toLowerCase().includes('team');

  const columns = [
    {
      title: '#', width: 55,
      render: (_: any, __: any, index: number) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{(currentPage - 1) * pageSize + index + 1}</Text>
      ),
    },
    {
      title: 'Approval ID', dataIndex: 'approvalId', key: 'approvalId', width: 110,
      render: (val: string) => <Text style={{ fontSize: 12, fontWeight: 600 }}>{val}</Text>,
    },
    {
      title: 'Award Type', dataIndex: 'rewardName', key: 'rewardName', width: 160,
      render: (val: string) => (
        <Tag color={val?.toLowerCase().includes('team') ? 'blue' : 'purple'} style={{ fontWeight: 600 }}>{val}</Tag>
      ),
    },
    {
      title: <Space><UserOutlined /> Nominee</Space>, dataIndex: 'nomineeName', key: 'nomineeName',
      sorter: (a: AwardRecord, b: AwardRecord) => a.nomineeName.localeCompare(b.nomineeName),
      render: (val: string, r: AwardRecord) => (
        <div>
          <Text style={{ fontSize: 13 }}>{val || r.teamName}</Text><br />
          <Text type="secondary" style={{ fontSize: 11 }}>{r.nomineeEmail}</Text>
        </div>
      ),
    },
    {
      title: 'Unit', dataIndex: 'nomineeUnit', key: 'nomineeUnit', width: 130,
      render: (val: string) => <Text style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: <Space><UserOutlined /> Nominator</Space>, dataIndex: 'nominatorName', key: 'nominatorName',
      sorter: (a: AwardRecord, b: AwardRecord) => a.nominatorName.localeCompare(b.nominatorName),
      render: (val: string, r: AwardRecord) => (
        <div>
          <Text style={{ fontSize: 13 }}>{val}</Text><br />
          <Text type="secondary" style={{ fontSize: 11 }}>{r.nominatorEmail}</Text>
        </div>
      ),
    },
    {
      title: 'Approval Status', key: 'votes', width: 200,
      render: (_: any, r: AwardRecord) => (
        <Space direction="vertical" size={2}>
          {[['Manager', r.managerVote], ['Unit Head', r.firstLevelApproverVote], ['ELT', r.secondLevelApproverVote], ['Final', r.finalLevelApproverVote]].map(([label, vote]) => (
            <Space key={label as string} size={4}>
              <Text style={{ fontSize: 11, color: '#888', minWidth: 60 }}>{label}:</Text>
              <Badge status={voteColor(vote as string) as any} text={<Text style={{ fontSize: 11 }}>{(vote as string) || 'Pending'}</Text>} />
            </Space>
          ))}
          {(!r.firstLevelApproverEmail || !r.secondLevelApproverEmailId) && (
            <Tag color="red" style={{ fontSize: 11, marginTop: 2 }}>
              {!r.firstLevelApproverEmail ? 'Unit Head not set' : 'ELT approver not set'}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Actions', key: 'actions', width: 130,
      render: (_: any, record: AwardRecord) => (
        <Space size={4} direction="vertical">
          <Space size={4}>
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined />} size="small" style={{ color: '#8629A4' }} onClick={() => openEdit(record)} />
            </Tooltip>
            {record.withdrawnAt && (
              <Popconfirm
                title="Restore this nomination?"
                description="It becomes active again, with its votes and comments as they were."
                okText="Restore"
                onConfirm={() => restore([record.id])}
              >
                <Tooltip title="Restore">
                  <Button type="text" icon={<UndoOutlined />} size="small" style={{ color: '#389e0d' }} />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
          {record.withdrawnAt && (
            <Tooltip title={
              <div>
                <div>Reason: {record.withdrawnReason}</div>
                <div>By: {record.withdrawnBy}</div>
                <div>On: {new Date(record.withdrawnAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' })} IST</div>
              </div>
            }>
              <Tag color="default" icon={<StopOutlined />}>Withdrawn</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: '1', label: 'Basic Info',
      children: (
        <Form.Item name="rewardName" label="Award Type">
          <Input disabled style={{ background: 'rgba(134,41,164,0.04)', color: '#333', fontWeight: 600 }} />
        </Form.Item>
      ),
    },
    {
      key: '2', label: isTeam ? 'Team' : 'Nominee',
      children: isTeam ? (
        <>
          <SectionHeader title="Team Details" />
          <Form.Item name="teamName" label="Team Name"><Input prefix={<TeamOutlined style={{ color: '#bbb' }} />} /></Form.Item>
          <Form.Item name="teamMembers" label="Team Members"><TextArea rows={4} /></Form.Item>
        </>
      ) : (
        <>
          <SectionHeader title="Nominee Details" />
          <G>
            <Form.Item name="nomineeName" label="Name"><Input /></Form.Item>
            <Form.Item name="nomineeEmail" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
            <Form.Item name="nomineeEmployeeId" label="Employee ID"><Input /></Form.Item>
            <Form.Item name="nomineeUnit" label="Unit"><Input /></Form.Item>
            <Form.Item name="nomineeDesignation" label="Designation"><Input /></Form.Item>
            <Form.Item name="nomineeGrade" label="Grade"><Input /></Form.Item>
            <Form.Item name="nomineeCountry" label="Country"><Input /></Form.Item>
            <Form.Item name="nomineeCity" label="City"><Input /></Form.Item>
            <Form.Item name="nomineeLegalEntityName" label="Legal Entity"><Input /></Form.Item>
            <Form.Item name="nomineeBu" label="Business Unit"><Input /></Form.Item>
            <Form.Item name="nomineeProductName" label="Product Name"><Input /></Form.Item>
          </G>
        </>
      ),
    },
    {
      key: '3', label: 'Nominator',
      children: (
        <>
          <SectionHeader title="Nominator Details" />
          <G>
            <Form.Item name="nominatorName" label="Name"><Input /></Form.Item>
            <Form.Item name="nominatorEmail" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
            <Form.Item name="nominatorEmployeeId" label="Employee ID"><Input /></Form.Item>
            <Form.Item name="nominatorUnit" label="Unit"><Input /></Form.Item>
            <Form.Item name="nominatorDesignation" label="Designation"><Input /></Form.Item>
            <Form.Item name="nominatorGrade" label="Grade"><Input /></Form.Item>
            <Form.Item name="nominatorCountry" label="Country"><Input /></Form.Item>
            <Form.Item name="nominatorCity" label="City"><Input /></Form.Item>
            <Form.Item name="nominatorLegalEntityName" label="Legal Entity"><Input /></Form.Item>
            <Form.Item name="nominatorProductName" label="Product Name"><Input /></Form.Item>
          </G>
          <Form.Item name="nominatorCitation" label="Citation"><TextArea rows={3} /></Form.Item>
          <Form.Item name="nominatorComments" label="Comments"><TextArea rows={2} /></Form.Item>
        </>
      ),
    },
    {
      key: '4', label: 'Manager',
      children: (
        <>
          <SectionHeader title="Manager" />
          <G>
            <Form.Item name="managerName" label="Name"><Input /></Form.Item>
            <Form.Item name="managerId" label="Manager ID"><Input /></Form.Item>
            <Form.Item name="managerEmail" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
            <Form.Item name="managerVote" label="Vote"><Select options={VOTE_OPTIONS} /></Form.Item>
          </G>
          <Form.Item name="managerComments" label="Comments"><TextArea rows={2} /></Form.Item>
        </>
      ),
    },
    {
      key: '5', label: 'Approvers',
      children: (
        <>
          <SectionHeader title="Unit Head (Level 1)" />
          <G>
            <Form.Item name="firstLevelApproverName" label="Name"><Input /></Form.Item>
            <Form.Item name="firstLevelApproverEmployeeId" label="Employee ID"><Input /></Form.Item>
            <Form.Item name="firstLevelApproverEmail" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
            <Form.Item name="firstLevelApproverUnit" label="Unit"><Input /></Form.Item>
            <Form.Item name="firstLevelApproverVote" label="Vote"><Select options={VOTE_OPTIONS} /></Form.Item>
          </G>
          <Form.Item name="firstLevelApproverComments" label="Comments"><TextArea rows={2} /></Form.Item>

          <SectionHeader title="ELT Reviewer (Level 2)" />
          <G>
            <Form.Item name="secondLevelApproverName" label="Name"><Input /></Form.Item>
            <Form.Item name="secondLevelApproverEmailId" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
            <Form.Item name="secondLevelApproverVote" label="Vote"><Select options={VOTE_OPTIONS} /></Form.Item>
            <Form.Item name="eltAverageScore" label="ELT Average Score"><Input /></Form.Item>
          </G>
          <Form.Item name="secondLevelApproverComments" label="Comments"><TextArea rows={2} /></Form.Item>

          <SectionHeader title="Final Level Approver" />
          <G>
            <Form.Item name="finalLevelApproverName" label="Name"><Input /></Form.Item>
            <Form.Item name="finalLevelApproverEmail" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
            <Form.Item name="finalLevelApproverVote" label="Vote"><Select options={VOTE_OPTIONS} /></Form.Item>
          </G>
          <Form.Item name="finalLevelApproverComments" label="Comments"><TextArea rows={2} /></Form.Item>
        </>
      ),
    },
    {
      key: '6', label: 'ELT Calibration',
      children: (
        <>
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="Empty slots are filled from Admin > ELT Emails (key elt_1_email = ELT Member 1, ...). Click Update to save them." />
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
            <div key={n}>
              <SectionHeader title={`ELT Member ${n}`} />
              <G>
                <Form.Item name={`elt${n}Name`} label="Name"><Input /></Form.Item>
                <Form.Item name={`elt${n}Email`} label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}><Input /></Form.Item>
                <Form.Item name={`elt${n}Response`} label="Response"><Select options={ELT_RESPONSE_OPTIONS} /></Form.Item>
                <Form.Item name={`elt${n}Score`} label="Score"><Input /></Form.Item>
              </G>
            </div>
          ))}
        </>
      ),
    },
    {
      key: '7', label: 'Other',
      children: (
        <>
          <Form.Item name="campaignName" label="Campaign Name"><Input /></Form.Item>
          <Form.Item name="formFields" label="Form Fields"><TextArea rows={4} /></Form.Item>
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Space wrap>
          <Select
            placeholder="Campaign" style={{ width: 260 }}
            value={campaignId || undefined}
            onChange={val => setCampaignId(val)}
            options={campaigns.map(c => ({
              value: c.id,
              label: `${c.campaignName || `Campaign ${c.id}`}${c.campaignStatus === 'active' ? ' (active)' : ''} · ${c.nominationCount}`,
            }))}
          />
          <Input.Search
            placeholder="Search nominee, nominator, approval ID…"
            allowClear style={{ width: 300 }}
            value={search} onChange={e => setSearch(e.target.value)}
          />
          <Select
            placeholder="Filter by award type" allowClear style={{ width: 200 }}
            value={rewardFilter || undefined} onChange={val => setRewardFilter(val ?? '')}
            options={rewardTypes.map(t => ({ value: t, label: t }))}
          />
          <Select
            placeholder="Approver check" allowClear style={{ width: 220 }}
            value={approverCheck || undefined} onChange={val => setApproverCheck((val ?? '') as ApproverCheck)}
            options={APPROVER_CHECKS.map(c => ({ value: c.value, label: `${c.label} (${activeRecords.filter(c.test).length})` }))}
          />
          <Select
            style={{ width: 190 }}
            value={show}
            onChange={setShow}
            options={[
              { value: 'active', label: `Active (${activeRecords.length})` },
              { value: 'withdrawn', label: `Withdrawn (${records.length - activeRecords.length})` },
              { value: 'all', label: `All (${records.length})` },
            ]}
          />
        </Space>
        <Space wrap>
          <Tooltip title={selectedActive.length ? 'Not required: hide from approvers, counts and reminders' : 'Tick active nominations first'}>
            <Button danger icon={<StopOutlined />} disabled={!selectedActive.length} onClick={openWithdraw}>
              Withdraw{selectedActive.length ? ` (${selectedActive.length})` : ''}
            </Button>
          </Tooltip>
          {selectedWithdrawn.length > 0 && (
            <Popconfirm
              title={`Restore ${selectedWithdrawn.length} nomination${selectedWithdrawn.length === 1 ? '' : 's'}?`}
              description="They become active again, with their votes and comments as they were."
              okText="Restore"
              onConfirm={() => restore(selectedWithdrawn.map(r => r.id))}
            >
              <Button icon={<UndoOutlined />} style={{ color: '#389e0d', borderColor: '#389e0d' }}>
                Restore ({selectedWithdrawn.length})
              </Button>
            </Popconfirm>
          )}
          <Text type="secondary" style={{ fontSize: 13 }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</Text>
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={loadRecords} loading={loading} />
          </Tooltip>
        </Space>
      </div>

      <Table
        rowKey="id" columns={columns} dataSource={filtered} loading={loading}
        rowSelection={{
          selectedRowKeys: selectedIds,
          preserveSelectedRowKeys: true,
          onChange: keys => setSelectedIds(keys as number[]),
        }}
        pagination={{
          current: currentPage, pageSize, showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: t => `Total ${t} records`,
          onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
        }}
        bordered size="middle"
        style={{ borderRadius: 8, overflow: 'hidden' }}
        rowClassName={(r: AwardRecord, index) => [index % 2 === 0 ? '' : 'ant-table-row-alt', r.withdrawnAt ? 'aceavia-row-withdrawn' : ''].join(' ').trim()}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={
          <Space>
            <EditOutlined style={{ color: '#8629A4' }} />
            <span>Edit Award — <Text style={{ color: '#8629A4' }}>#{editing?.approvalId}</Text></span>
          </Space>
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="Update"
        confirmLoading={saving}
        okButtonProps={{ style: { background: '#8629A4', borderColor: '#8629A4' } }}
        destroyOnClose
        width={800}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 } }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Tabs items={tabItems} size="small" />
        </Form>
      </Modal>

      <Modal
        title={<Space><StopOutlined style={{ color: '#cf1322' }} />Withdraw {selectedActive.length} nomination{selectedActive.length === 1 ? '' : 's'}</Space>}
        open={withdrawOpen}
        onOk={saveWithdraw}
        onCancel={() => setWithdrawOpen(false)}
        okText="Withdraw"
        okButtonProps={{ danger: true }}
        confirmLoading={bulkSaving}
        destroyOnClose
        width={560}
      >
        <Alert type="warning" showIcon style={{ marginBottom: 16 }}
          message="Withdrawn nominations are hidden from approvers, counts, downloads and reminder emails, and cannot be voted on."
          description="Nothing is deleted: votes, comments and ELT scores are kept, Load nominations will not add them again, and Restore brings them back as they were." />
        <div style={{ marginBottom: 12, maxHeight: 140, overflowY: 'auto' }}>
          {selectedActive.map(r => (
            <div key={r.id}><Text strong>#{r.approvalId}</Text> <Text>{r.nomineeName || r.teamName}</Text> <Text type="secondary">({r.rewardName})</Text></div>
          ))}
        </div>
        <Form form={withdrawForm} layout="vertical" preserve={false}>
          <Form.Item name="reason" label="Reason" rules={[{ required: true, whitespace: true, message: 'Enter the reason for withdrawing' }]}>
            <TextArea rows={3} maxLength={500} showCount placeholder="e.g. Duplicate nomination, nominee left the company" />
          </Form.Item>
        </Form>
      </Modal>

      <style>{`.aceavia-row-withdrawn td { color: #999 !important; background: #fafafa !important; }`}</style>
    </div>
  );
};

export default AceAviaAwards;
